"use client";
import { motion, useTransform } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import type { ImageProps } from "@/types/image";
import type { LinkData } from "@/types/link";
import { useScrollParallax } from "@/lib/hooks";
import { Link } from "@/components/ui/Link";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { Image } from "../Image";
import { Locale, getTranslations } from "@/lib/i18n/config";

const contactCardVariants = cva("", {
  variants: {
    variant: {
      light: "",
      dark: "",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const nameColorVariants = cva("headline-4 ", {
  variants: {
    variant: {
      light: "text-white",
      dark: "text-black",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const textColorVariants = cva("text-lg uppercase", {
  variants: {
    variant: {
      light: "text-white",
      dark: "text-gray-dark",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const backgroundVariants = cva("absolute bottom-0 w-full rounded-md", {
  variants: {
    variant: {
      light: "bg-white/20 backdrop-blur-md",
      dark: "bg-off-white",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface ContactCardProps extends VariantProps<
  typeof contactCardVariants
> {
  /**
   * Contact person's name
   */
  name: string;
  /**
   * Contact person's position/title
   */
  position?: string;
  /**
   * Profile image
   */
  image: ImageProps;
  /**
   * Link data for the contact card
   */
  link?: LinkData;
  /**
   * Email address for contact (only shown in parallax variant)
   */
  email?: string;
  /**
   * Enable parallax effect
   * @default false
   */
  parallax?: boolean;
  /**
   * Color variant
   * @default "light"
   */
  variant?: "light" | "dark";
  /**
   * Show link at the end of the card
   * @default true
   */
  showLink?: boolean;
  /**
   * Current locale
   */
  locale: Locale;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * ContactCard component - Square card displaying contact information
 *
 * Features:
 * - Square aspect ratio with rounded corners
 * - Profile image background
 * - Name overlay at the bottom
 * - Clickable link wrapper
 * - Parallax scroll effect (when parallax prop is true)
 *
 * Usage:
 * ```tsx
 * <ContactCard
 *   name="Max Mustermann"
 *   image={{ src: "/team/max.jpg", alt: "Max Mustermann" }}
 *   link={{ label: "View profile", href: "/team/max" }}
 *   parallax
 * />
 * ```
 */
export const ContactCard: React.FC<ContactCardProps> = ({
  name,
  position,
  image,
  link,
  email,
  parallax = false,
  variant = "light",
  showLink = true,
  locale,
  className = "",
}) => {
  const { common: t } = getTranslations(locale);
  const { ref: containerRef, value: backgroundProgress } = useScrollParallax(
    90,
    75
  );
  // Fed to `style`, not `animate`: the parallax value already updates once per
  // frame, so a tween on top of it only queued a second animation per frame.
  const backgroundHeight = useTransform(
    backgroundProgress,
    (progress) => `${progress}%`
  );

  // Parallax variant - different HTML structure for parallax effect
  if (parallax) {
    return (
      <div ref={containerRef} className="relative flex flex-col gap-sm group">
        <div className="relative rounded-md overflow-hidden">
          <motion.div
            className={backgroundVariants({ variant })}
            style={{ height: backgroundHeight }}
          />
          <Image
            {...image}
            sizes="30vw"
            className="relative w-full aspect-square object-contain"
          />
          {link?.href && (
            <motion.div
              className="absolute translate-y-[calc(100%+var(--spacing-sm))] right-sm z-10"
              style={{ bottom: backgroundHeight }}
            >
              <IconButton
                icon="paperplane"
                href={`${link.href}#kontakt`}
                ariaLabel={t.contactPerson.replace("{{name}}", name)}
                color={variant === "light" ? "yellow" : "purple"}
                size={16}
                className="group-hover:scale-110 transition-all"
              />
            </motion.div>
          )}
        </div>
        <div className="flex flex-col gap-xs">
          <p className={nameColorVariants({ variant })}>{name}</p>
          {position && (
            <p className={textColorVariants({ variant })}>{position}</p>
          )}
          {showLink && link?.href && (
            <Link
              {...link}
              icon="arrow-right"
              color={variant === "light" ? "yellow" : "purple-medium"}
            />
          )}
        </div>
      </div>
    );
  }

  // Standard variant - simpler structure without parallax
  const CardContent = (
    <>
      <Image {...image} sizes="20vw" className="w-full h-full object-cover" />
      <div className="absolute opacity-0 group-hover:opacity-100 inset-0 flex flex-col gap-xs justify-center p-md transition-opacity duration-300 bg-purple-dark/70">
        <p className="w-full text-white text-center headline-5">{name}</p>
        {position && (
          <p className="w-full text-white text-center uppercase">{position}</p>
        )}
      </div>
    </>
  );

  const cardClasses = `block aspect-square rounded-md overflow-hidden relative group ${className}`;

  if (!showLink || !link?.href) {
    return <div className={cardClasses}>{CardContent}</div>;
  }

  return (
    <Link
      href={link.href}
      target={link.target}
      rel={link.rel}
      label={link.label || ""}
      ariaLabel={link.ariaLabel || `${name} - ${link.label}`}
      className={cardClasses}
    >
      {CardContent}
    </Link>
  );
};
